import React, { useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { IUser } from '../../types/damTypes';
import { hasPermission } from '../../utils/permissionUtils';
import { CONTROL, PRES } from './chrome';
import { COPY } from './presentationCopy';
import PresentationsEditor from './editor/PresentationsEditor';
import TemplatesHome from './TemplatesHome';
import LibraryHome from './LibraryHome';
import CatalogHome from './CatalogHome';
import PresentationsAdminPage from './PresentationsAdminPage';
import { PresentationSettingsProvider } from './PresentationSettingsContext';
import { PresentationCatalog } from './types';

interface PresentationsModuleProps {
  onClose: () => void;
  currentUser: IUser | null;
}

type EditorReturn = { kind: 'deck'; id: string; name?: string; catalog?: PresentationCatalog };

type Workspace =
  | { kind: 'deck'; id: string; catalog?: PresentationCatalog; focusSlideId?: string; focusObjectId?: string }
  | { kind: 'salesSheets' }
  | { kind: 'templates' }
  | { kind: 'template'; templateId: string; layoutId?: string; returnTo?: EditorReturn }
  | { kind: 'library' }
  | { kind: 'librarySlide'; librarySlideId: string; returnTo?: EditorReturn }
  | { kind: 'admin'; returnTo?: Exclude<Workspace, { kind: 'admin' }> | null };

const PresentationsModuleBody: React.FC<PresentationsModuleProps> = ({ onClose, currentUser }) => {
  const canRead = hasPermission(currentUser, 'presentations:read');
  const canCreate = hasPermission(currentUser, 'presentations:create');
  const canEdit = hasPermission(currentUser, 'presentations:edit');
  const canManageCategories = hasPermission(currentUser, 'presentations:admin') || hasPermission(currentUser, 'admin:all');
  const canManageTemplates = hasPermission(currentUser, 'presentations:manageTemplates');
  const [workspace, setWorkspace] = useState<Workspace | null>(null);

  const openAdmin = (returnTo: Exclude<Workspace, { kind: 'admin' }> | null) => {
    setWorkspace({ kind: 'admin', returnTo });
  };

  if (!canRead) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3, bgcolor: PRES.surfaceSunken }}>
        <Box sx={{ p: 3, maxWidth: 520, bgcolor: PRES.surface, border: `1px solid ${PRES.borderSubtle}`, borderRadius: `${CONTROL.radiusCard}px` }}>
          <Alert severity="warning" sx={{ mb: 2 }}>{COPY.missingRead}</Alert>
          <Button variant="contained" onClick={onClose} sx={{ bgcolor: PRES.interactive, boxShadow: 'none', textTransform: 'none' }}>{COPY.backToAthena}</Button>
        </Box>
      </Box>
    );
  }

  if (workspace?.kind === 'admin') {
    const back = workspace.returnTo;
    const onBackLabel = back?.kind === 'deck'
      ? COPY.backToPresentation
      : back?.kind === 'templates' || back?.kind === 'template'
        ? COPY.templates
        : back?.kind === 'library' || back?.kind === 'librarySlide'
          ? COPY.slideLibrary
          : back?.kind === 'salesSheets'
            ? COPY.allSalesSheets
            : COPY.allPresentations;
    return (
      <PresentationsAdminPage
        onBack={() => setWorkspace(workspace.returnTo ?? null)}
        onBackLabel={onBackLabel}
      />
    );
  }

  if (workspace?.kind === 'deck') {
    const catalog = workspace.catalog === 'salesSheets' ? 'salesSheets' : 'presentations';
    return (
      <PresentationsEditor
        presentationId={workspace.id}
        currentUser={currentUser}
        onBack={() => {
          setWorkspace(catalog === 'salesSheets' ? { kind: 'salesSheets' } : null);
        }}
        onBackLabel={catalog === 'salesSheets' ? COPY.allSalesSheets : COPY.allPresentations}
        onOpenSettings={canManageCategories ? () => openAdmin(workspace) : undefined}
        onOpenTemplates={canManageTemplates ? () => setWorkspace({ kind: 'templates' }) : undefined}
        onOpenLibrarySlides={canManageTemplates ? () => setWorkspace({ kind: 'library' }) : undefined}
        onEditTemplate={canManageTemplates ? (templateId, layoutId, fromName) => {
          setWorkspace({
            kind: 'template',
            templateId,
            layoutId,
            returnTo: { kind: 'deck', id: workspace.id, name: fromName, catalog },
          });
        } : undefined}
        focusSlideId={workspace.focusSlideId}
        focusObjectId={workspace.focusObjectId}
        onOpenPresentation={(id, focus) => setWorkspace({
          kind: 'deck',
          id,
          catalog,
          focusSlideId: focus?.focusSlideId,
          focusObjectId: focus?.focusObjectId,
        })}
      />
    );
  }

  if (workspace?.kind === 'template') {
    return (
      <PresentationsEditor
        templateEdit={{ templateId: workspace.templateId, layoutId: workspace.layoutId }}
        currentUser={currentUser}
        onBack={() => {
          if (workspace.returnTo?.kind === 'deck') {
            setWorkspace({
              kind: 'deck',
              id: workspace.returnTo.id,
              catalog: workspace.returnTo.catalog,
            });
            return;
          }
          setWorkspace({ kind: 'templates' });
        }}
        onBackLabel={workspace.returnTo?.kind === 'deck'
          ? (workspace.returnTo.name?.trim() || COPY.backToPresentation)
          : COPY.templates}
        onOpenSettings={canManageCategories ? () => openAdmin(workspace) : undefined}
      />
    );
  }

  if (workspace?.kind === 'librarySlide') {
    return (
      <PresentationsEditor
        libraryEdit={{ librarySlideId: workspace.librarySlideId }}
        currentUser={currentUser}
        onBack={() => {
          if (workspace.returnTo?.kind === 'deck') {
            setWorkspace({
              kind: 'deck',
              id: workspace.returnTo.id,
              catalog: workspace.returnTo.catalog,
            });
            return;
          }
          setWorkspace({ kind: 'library' });
        }}
        onBackLabel={workspace.returnTo?.kind === 'deck'
          ? (workspace.returnTo.name?.trim() || COPY.backToPresentation)
          : COPY.slideLibrary}
        onOpenSettings={canManageCategories ? () => openAdmin(workspace) : undefined}
      />
    );
  }

  if (workspace?.kind === 'library') {
    return (
      <LibraryHome
        onBack={() => setWorkspace(null)}
        onOpen={(librarySlideId) => setWorkspace({ kind: 'librarySlide', librarySlideId })}
        onOpenSettings={canManageCategories ? () => openAdmin(workspace) : undefined}
      />
    );
  }

  if (workspace?.kind === 'templates') {
    return (
      <TemplatesHome
        onBack={() => setWorkspace(null)}
        onOpen={(templateId, layoutId) => setWorkspace({ kind: 'template', templateId, layoutId })}
        onOpenSettings={canManageCategories ? () => openAdmin(workspace) : undefined}
      />
    );
  }

  const catalog: PresentationCatalog = workspace?.kind === 'salesSheets' ? 'salesSheets' : 'presentations';

  return (
    <CatalogHome
      catalog={catalog}
      canCreate={canCreate}
      canEdit={canEdit}
      canManageCategories={canManageCategories}
      canManageTemplates={canManageTemplates}
      onClose={catalog === 'salesSheets' ? () => setWorkspace(null) : onClose}
      onOpen={(id) => setWorkspace({ kind: 'deck', id, catalog })}
      onOpenSalesSheets={catalog === 'presentations' ? () => setWorkspace({ kind: 'salesSheets' }) : undefined}
      onOpenTemplates={canManageTemplates ? () => setWorkspace({ kind: 'templates' }) : undefined}
      onOpenLibrary={canManageTemplates ? () => setWorkspace({ kind: 'library' }) : undefined}
      onOpenSettings={canManageCategories
        ? () => openAdmin(catalog === 'salesSheets' ? { kind: 'salesSheets' } : null)
        : undefined}
    />
  );
};

const PresentationsModule: React.FC<PresentationsModuleProps> = (props) => (
  <PresentationSettingsProvider>
    <PresentationsModuleBody {...props} />
  </PresentationSettingsProvider>
);

export default PresentationsModule;
